import { ChatMessageHistoryButton } from '@/features/ChatDialog/EditMessage/ChatMessageHistoryButton';
import { useAuth } from '@/entities/Auth/store';
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { ChatConversation } from '@/entities/Chat/ui/ChatConversation';
import { ImagePreviewDialog } from '@/shared/ui/ImagePreviewDialog/ImagePreviewDialog';
import { appDialogToCompanion } from '@/entities/AppChat/mappers';
import { createUserChatCompanionStore } from '@/entities/UserChat/model/companionStore';
import {
    appDialogToUserChatSummary,
    appMessageToUserChatMessage,
    groupUserChatMessages,
} from '@/entities/UserChat/mappers';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import { useChatDialogRoute } from '@/features/ChatDialog/Workspace/useChatDialogRoute';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { RequestErrorBanner, RequestLoading } from '@/shared/ui/RequestState';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { Link } from '@/shared/ui/Link/Link';
import { formatMoscowTime } from '@/shared/lib/dateTime';
import type { UserChatReaderProps } from '../model/types';
import { UserChatList } from './UserChatList';
import classes from './UserChatViewer.module.scss';

/** Показывает время сообщения по Москве в прежнем представлении переписки */
const formatMessageTime = (timestamp: number): string => Number.isFinite(timestamp)
    ? formatMoscowTime(new Date(timestamp).toISOString())
    : '';

/** Отображает список и сообщения владельца в прежнем режиме просмотра */
export const UserChatReader = (
    { enabled, realtime, routeError, ownerAppUserId }: UserChatReaderProps
) => {
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const route = useChatDialogRoute(enabled && realtime.connection !== 'connecting');

    const state = useChatDialogWorkspace(useShallow((workspace) => ({
        dialogs: workspace.dialogs,
        selectedDialog: workspace.selectedDialog,
        messages: workspace.messages,
        listStatus: workspace.listStatus,
        listError: workspace.listError,
        listMoreStatus: workspace.listMoreStatus,
        listMoreError: workspace.listMoreError,
        nextOffset: workspace.nextOffset,
        messagesStatus: workspace.messagesStatus,
        messagesError: workspace.messagesError,
        historyStatus: workspace.historyStatus,
        historyError: workspace.historyError,
        refreshError: workspace.refreshError,
        refreshStatus: workspace.refreshStatus,
        nextBeforeSequence: workspace.nextBeforeSequence,
        nextAfterSequence: workspace.nextAfterSequence,
        fetchDialogs: workspace.fetchDialogs,
        fetchMoreDialogs: workspace.fetchMoreDialogs,
        fetchMessages: workspace.fetchMessages,
        fetchOlderMessages: workspace.fetchOlderMessages,
        fetchNewerMessages: workspace.fetchNewerMessages,
        refreshLive: workspace.refreshLive,
    })));

    const chats = useMemo(() => state.dialogs.map(appDialogToUserChatSummary), [state.dialogs]);
    const messages = useMemo(() => state.messages.map(appMessageToUserChatMessage), [state.messages]);
    const groups = useMemo(() => groupUserChatMessages(messages), [messages]);

    const selected = state.selectedDialog
        ? appDialogToUserChatSummary(state.selectedDialog)
        : null;

    const companion = appDialogToCompanion(state.selectedDialog);
    const companionId = companion?.id;
    const companionRole = companion?.role;
    const dialogId = state.selectedDialog?.id;
    const { fetchDialogs } = state;
    const canViewClients = useCanAccessPermission('admin.clients.view');
    const canViewExecutors = useCanAccessPermission('admin.executors.view');

    const canViewCompanion = companion?.role === 'Client'
        ? canViewClients
        : canViewExecutors;

    const profileStore = useMemo(() => createUserChatCompanionStore(), []);
    const profile = useStore(profileStore);
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    useEffect(() => {
        if (enabled && realtime.connection === 'disabled') void fetchDialogs();
    }, [enabled, realtime.connection, fetchDialogs, sessionRevision]);

    useEffect(() => {
        setImageUrl(null);

        if (enabled && companionId && companionRole && canViewCompanion) void profileStore.getState().load(companionId, companionRole);
        return () => profileStore.getState().reset();
    }, [enabled, companionId, companionRole, dialogId, canViewCompanion, profileStore]);

    /** Закрывает просмотр фотографии и возвращает фокус в переписку */
    const closeImage = useCallback(() => setImageUrl(null), []);

    /** Открывает диалог без подтверждения прочтения от имени владельца */
    const selectChat = (id: string): void => {
        const dialog = state.dialogs.find((item) => item.id === id);

        if (dialog) route.selectDialog(dialog);
    };

    const historyLoading = state.historyStatus === RequestStatus.Loading;
    const listError = routeError ?? state.listError ?? realtime.error;

    return <>
        <UserChatList
            owner={enabled}
            chats={enabled
                ? chats
                : []
            }
            selectedChatId={enabled
                ? state.selectedDialog?.id ?? null
                : null
            }
            status={!enabled
                ? RequestStatus.Idle
                : listError
                    ? RequestStatus.Error
                    : state.listStatus === RequestStatus.Idle
                        ? RequestStatus.Loading
                        : state.listStatus
            }
            error={listError}
            hasMore={enabled && state.nextOffset !== null}
            loadingMore={state.listMoreStatus === RequestStatus.Loading}
            onSelect={selectChat}
            onRetry={realtime.error
                ? realtime.retry
                : state.fetchDialogs
            }
            onMore={state.fetchMoreDialogs}
        />
        <ChatConversation
            companion={enabled
                ? selected?.companion ?? null
                : null
            }
            messages={enabled
                ? messages
                : []
            }
            groups={groups}
            renderMessageMetadata={(message) => {
                const original = state.messages.find((item) => item.id === message.id);

                return dialogId && original?.editedAt
                    ? <ChatMessageHistoryButton
                        dialogId={dialogId}
                        messageId={message.id}
                        ownerAppUserId={ownerAppUserId}
                    />
                    : null;
            }}
            formatMessageTime={formatMessageTime}
            status={state.messagesStatus}
            error={state.messagesError}
            readOnly
            onRetryConversation={state.fetchMessages}
            onOpenImage={setImageUrl}
            headerActions={companion && canViewCompanion &&
                <div className={classes.profileActions}>
                    {profile.status === RequestStatus.Loading &&
                        <RequestLoading message="Ищем профиль..." />
                    }
                    <RequestErrorBanner
                        status={profile.status}
                        error={profile.error}
                        fallbackMessage="Не удалось найти профиль собеседника"
                        onRetry={() => profileStore.getState().load(companion.id, companion.role)}
                    />
                    {profile.profile &&
                        <>
                            <span>Телефон: {profile.profile.phone ?? 'не указан'}
                            </span>
                            <Link
                                url={profile.profile.url}
                                text="Открыть профиль собеседника"
                                isBlank
                            />
                        </>
                    }
                </div>
            }
            historyControls={enabled &&
                <>
                    <RequestErrorBanner
                        status={state.refreshStatus}
                        error={state.refreshError}
                        fallbackMessage="Не удалось обновить переписку"
                        onRetry={state.refreshLive}
                    />
                    <RequestErrorBanner
                        status={state.historyStatus}
                        error={state.historyError}
                        fallbackMessage="Не удалось загрузить страницу сообщений"
                        onRetry={state.nextAfterSequence !== null
                            ? state.fetchNewerMessages
                            : state.fetchOlderMessages
                        }
                    />
                    <RequestErrorBanner
                        status={state.listMoreStatus}
                        error={state.listMoreError}
                        fallbackMessage="Не удалось загрузить переписки"
                        onRetry={state.fetchMoreDialogs}
                    />
                    {(state.nextBeforeSequence !== null || state.nextAfterSequence !== null) &&
                        <div className={classes.historyControls}>
                            {state.nextBeforeSequence !== null &&
                                <ActionButton
                                    label="Предыдущие сообщения"
                                    color="secondary"
                                    loading={historyLoading}
                                    disabled={historyLoading}
                                    onClick={state.fetchOlderMessages}
                                />
                            }
                            {state.nextAfterSequence !== null &&
                                <ActionButton
                                    label="Следующие сообщения"
                                    color="secondary"
                                    loading={historyLoading}
                                    disabled={historyLoading}
                                    onClick={state.fetchNewerMessages}
                                />
                            }
                        </div>
                    }
                </>
            }
        />
        <ImagePreviewDialog
            imageUrl={imageUrl}
            alt="Вложение к сообщению в полном размере"
            onClose={closeImage}
        />
    </>;
};
